import MyTimeTile from "./MyTimeTile";
import {
  CustomersQV,
  JobsQV,
  ServiceTicketsQV,
  QuotesQV,
  ClosedJobsQV,
  InvoicesQV,
  ContractsQV,
  POsQV,
  VendorsQV,
  MarginQV,
  MessagesQV,
  RecentActivityQV,
  PMDueSoonQV,
  PMOverdueQV,
} from "./quadrantViews";

// Single source of truth for every grid tile on the home dashboard.
// Adding a tile = one entry here + (optionally) a derived field in
// computeDashboardData. The DashboardPage renders by mapping this
// registry; phases 1+ swap in user-customizable layouts without
// touching any tile definition.
//
// Per-entry contract:
//   id          — stable string key (used by future layout config)
//   title       — header shown on the tile
//   category    — grouping hint for the future sidebar/picker
//   visible     — (data, props) => bool; default true
//   onClickKey  — name of the callback prop on DashboardPage to wire
//   QuadrantView — optional component for the 2x2 quadrant; renders a
//                  mini-view (top-N rows etc.) instead of just the
//                  flat tile. Falls back to the standard tile if absent.
//   EITHER:
//     select    — (data) => { count?, sublabel?, htmlTitle? }; default
//                 path renders through the shared DashboardTile
//   OR:
//     component — custom render. Gets (data, onClick) as props. Used
//                 for tiles whose content isn't a simple count, e.g.
//                 MyTime pie chart.

export const TILE_REGISTRY = {
  customers: {
    id: "customers",
    title: "Customers",
    category: "service",
    visible: () => true,
    select: (d) => ({ count: d.totalCustomers }),
    QuadrantView: CustomersQV,
    onClickKey: "onShowCustomers",
  },

  jobs: {
    id: "jobs",
    title: "Jobs",
    category: "service",
    visible: () => true,
    select: (d) => ({ count: d.activeJobCount, sublabel: "Open / active" }),
    QuadrantView: JobsQV,
    onClickKey: "onShowJobs",
  },

  serviceTickets: {
    id: "serviceTickets",
    title: "Service Tickets",
    category: "service",
    visible: () => true,
    select: (d) => ({
      count: d.scopedTickets.filter((t) => !t.acknowledged).length,
      sublabel: "Unacknowledged",
    }),
    QuadrantView: ServiceTicketsQV,
    onClickKey: "onShowServiceTickets",
  },

  quotes: {
    id: "quotes",
    title: "Quotes",
    category: "billing",
    visible: (d) => d.canCreateQuotes,
    select: (d) => ({
      count: d.scopedQuotes.filter((q) => q.status !== "approved").length,
      sublabel: "Open quotes",
    }),
    QuadrantView: QuotesQV,
    onClickKey: "onShowQuotes",
  },

  invoices: {
    id: "invoices",
    title: "Invoices",
    category: "billing",
    visible: (_, props) => !!props.onShowInvoices,
    select: (d) => ({ count: d.issuedInvoiceCount, sublabel: "Issued total" }),
    QuadrantView: InvoicesQV,
    onClickKey: "onShowInvoices",
  },

  closedJobs: {
    id: "closedJobs",
    title: "Closed Jobs",
    category: "billing",
    // Gated by the same cap that lets billing edit/create bills.
    // Default-on in Billing dept preset; admin always.
    visible: (_, props) => !!props.onShowClosedJobs,
    select: (d) => ({
      count: d.closedJobsAwaitingBill.length,
      sublabel: "Awaiting bill",
    }),
    QuadrantView: ClosedJobsQV,
    onClickKey: "onShowClosedJobs",
  },

  contracts: {
    id: "contracts",
    title: "Contracts",
    category: "maintenance",
    visible: (_, props) => !!props.onShowContracts,
    select: (d) => ({
      count: d.contractsRaw.filter((c) => c.status === "active").length,
      sublabel: "Active PM",
    }),
    QuadrantView: ContractsQV,
    onClickKey: "onShowContracts",
  },

  purchaseOrders: {
    id: "purchaseOrders",
    title: "Purchase Orders",
    category: "operations",
    visible: () => true,
    select: (d) => ({
      count: d.scopedPOs.filter((p) => p.status !== "received").length,
      sublabel: "Open POs",
    }),
    QuadrantView: POsQV,
    onClickKey: "onShowPOs",
  },

  vendors: {
    id: "vendors",
    title: "Vendors",
    category: "operations",
    visible: (_, props) => !!props.onShowVendors,
    select: (d) => ({
      count: d.vendorsRaw.filter((v) => v.status === "active").length,
      sublabel: "Active",
    }),
    QuadrantView: VendorsQV,
    onClickKey: "onShowVendors",
  },

  margin: {
    id: "margin",
    title: "Margin Report",
    category: "billing",
    visible: (_, props) => !!props.onShowMargin,
    select: () => ({
      sublabel: "Customer / job type / month",
      htmlTitle:
        "Realized margin by customer / job type / month / quarter / year",
    }),
    QuadrantView: MarginQV,
    onClickKey: "onShowMargin",
  },

  messages: {
    id: "messages",
    title: "Messages",
    category: "communication",
    visible: () => true,
    select: (d) => ({ count: d.unreadMessageCount, sublabel: "Unread" }),
    QuadrantView: MessagesQV,
    onClickKey: "onShowMessages",
  },

  recentActivity: {
    id: "recentActivity",
    title: "Recent Activity",
    category: "service",
    visible: (d) => d.canSeeRecentActivity,
    select: (d) => ({
      count: d.recentRecordCount,
      sublabel: "Unacknowledged · last 7 days",
    }),
    QuadrantView: RecentActivityQV,
    onClickKey: "onShowRecentActivity",
  },

  pmDueSoon: {
    id: "pmDueSoon",
    title: "PM Due Soon",
    category: "maintenance",
    visible: () => true,
    select: (d) => ({ count: d.dueSoonPmCount, sublabel: "Within 30 days" }),
    QuadrantView: PMDueSoonQV,
    onClickKey: "onShowPmDueSoon",
  },

  pmOverdue: {
    id: "pmOverdue",
    title: "PM Overdue",
    category: "maintenance",
    visible: () => true,
    select: (d) => ({ count: d.overduePmCount }),
    QuadrantView: PMOverdueQV,
    onClickKey: "onShowPmOverdue",
  },

  myTime: {
    id: "myTime",
    title: "My Time",
    category: "personal",
    // Hide for users who have never clocked in -- no point showing a
    // permanent "0:00" tile to execs / admins who don't track time.
    // Self-cleaning: first clock-in flips this on.
    visible: (d) => d.myHasAnyTimeEver,
    component: MyTimeTile,
    // Sidebar shows total hours this week as a compact text badge
    // ("12:45") since the pie chart only fits in the quadrant.
    compactCount: (d) => {
      const total = d.myTimeStraight + d.myTimeOvertime + d.myTimeDouble;
      if (!total) return "0:00";
      const h = Math.floor(total / 60);
      const m = total % 60;
      return `${h}:${String(m).padStart(2, "0")}`;
    },
    onClickKey: "onShowMyTime",
  },
};

export const ALL_TILE_IDS = Object.keys(TILE_REGISTRY);

// Sidebar / picker grouping. Order matters — sidebar renders top-down.
export const CATEGORY_ORDER = [
  "service",
  "maintenance",
  "billing",
  "operations",
  "communication",
  "personal",
];

export const CATEGORY_LABELS = {
  service: "Service",
  maintenance: "Maintenance",
  billing: "Billing",
  operations: "Operations",
  communication: "Communication",
  personal: "Personal",
};

// Visible-tile ids grouped by category, in CATEGORY_ORDER order. Each
// inner array preserves registry insertion order, so the sidebar UI
// matches the order tiles are declared in this file.
export function getVisibleTilesByCategory(data, props) {
  const out = [];
  for (const cat of CATEGORY_ORDER) {
    const ids = ALL_TILE_IDS.filter((id) => {
      const t = TILE_REGISTRY[id];
      return t.category === cat && t.visible(data, props);
    });
    if (ids.length > 0) out.push({ category: cat, ids });
  }
  return out;
}
